// 鶏の待ちの間の移動を跳ねにする (2026-11-03)。11-04 作者 回数・高さ・跳び越え＝ok・ship=yes → 配布版の既定。前の版 (立ち絵のまま滑る) は DEV ?roosterMove=slide。
//   作者 (11-04 答えるページ blade-backstep-1104・rooster=fix) 原文:
//   「このキャラクターの移動は跳ねがいいです。前に詰めるときも跳ねるし、なんならたまに主人公の背中側まで跳ねてほしいです。」
//   配布版の鶏は歩きの絵を持たず、待ち (idle) の寄り (好みの間合い 150 より遠い時・256px/秒) と
//   立て直しの下がり (重なった時・2倍速) を、立ち絵のまま床を滑って動く (src/boss.ts の汎用の2分岐)。
//   この版はその2つを「跳ね」に替え、時々主人公を跳び越えて背中側へ降りる。数値はすべて仮 (微調整は作者の答えの後)。
//   絵は今ある4枚 (立ち・着地して走り出す・飛び上がり・宙) だけ。新しく描いた絵は無い。
//   当たり: 体の押し合いは配布版で切ってある (BOSS_CORE.enabled=false) ので跳び越えは素通り。
//     主人公は待ちの間ずっとボスの方へ向き直る (src/player.ts) ので、背中側へ降りた時は主人公も振り向く。
//     宙で高さ 72px を越える間は主人公の攻撃が当たらない (src/combat.ts の高さの判定がそのまま効く)。
//   跳んでいる間は攻撃に入らない。降りてから ROOSTER_HOP.restMs 待ってから次の跳びか技へ。跳び越えの後だけ少し長く待つ。
//   宙では向きを変えず、降りた tick に主人公の方へ向き直る (src/boss.ts の reFaceNow が roosterHopLocksFacing を見る)。
// 記録 = docs/research/2026-11-03-rooster-hop-move.md
import type { Boss, Player } from './types'

const BOSS_ID = 'inverted-jester'
let enabled = true
/** 既定 true。`?roosterMove=slide` の時だけ false (src/main.ts の DEV の門の中だけが呼ぶ)。 */
export function setRoosterHopMove(value: string | boolean | null): void {
  enabled = !(value === false || value === 'slide')
}
export function usesRoosterHopMove(boss: Pick<Boss, 'id'>): boolean { return enabled && boss.id === BOSS_ID }

export type RoosterHopKind = 'fwd' | 'back' | 'over'
export type RoosterHop = {
  kind: RoosterHopKind
  fromX: number
  toX: number
  elapsedMs: number
  /** 宙の長さ。その後 restMs だけ床で待つ。 */
  airMs: number
  restMs: number
  /** 首の羽 (胴) が立ち絵より上がる高さ。足の高さは絵ごとのずらしを足して出す (roosterHopAltitude)。 */
  risePx: number
  /** 前の跳び越えから数えた跳びの数 (跳び越えを続けて出さない)。 */
  sinceOver: number
  /** 何回目の跳びか (抽選の種)。 */
  n: number
}

/** 仮の数値表 (1か所)。px・ms。 */
export const ROOSTER_HOP = {
  /** 前へ詰める1跳びの最大の距離と宙の長さ。 */
  fwdPx: 140, fwdAirMs: 340, fwdRisePx: 40,
  /** 立て直しで下がる1跳び。 */
  backPx: 120, backAirMs: 360, backRisePx: 30,
  /** 跳び越え: 主人公の向こうの、好みの間合いの所へ降りる。 */
  overAirMs: 650, overRisePx: 150,
  /** 跳び越えを選ぶ割合と、出せる距離・壁の余り。前の跳び越えから minHopsBetween 回は跳び越えない。 */
  overChance: 0.3, overMaxDistPx: 320, overMinLandGapPx: 110, minHopsBetween: 2,
  /** 降りてから次の跳び・技までの間。跳び越えの後は主人公が振り向く間を足す。 */
  restMs: 150, overRestMs: 380,
  /** 降りた直後に「着地して走り出す」絵を出す長さ。 */
  landPoseMs: 90,
} as const

// 絵 (src/rooster_crow_art.ts と同じファイル名)。宙の絵の切り替えで胴が跳ばないよう、首の羽の高さの差を足の高さへ足す。
//   差 (ゲームの px) = (立ち絵 442 − その絵の首の羽の高さ) × 180/690。立ち 0・飛び上がり −28・宙 +46 は宙廻りと同じ値。
//   着地して走り出す絵は今回測った (色の重心で 宙より約 26 低い → 宙廻りの物差しへ直して約 224 → +57)。
const IDLE = 'rooster_idle_1027'
const LAND_RUN = 'rooster_land_run_1027'
const FLY_UP = 'rooster_fly_up_1027'
const HOVER = 'rooster_hover_1027'
const RUFF_SHIFT: Record<string, number> = { [IDLE]: 0, [LAND_RUN]: 57, [FLY_UP]: -28, [HOVER]: 46 }

function hash01(n: number): number { const s = Math.sin(n * 127.1 + 31.7) * 43758.5453; return s - Math.floor(s) }

export function roosterHopInAir(h: RoosterHop | undefined): boolean { return !!h && h.elapsedMs < h.airMs }
/** 宙では向きを変えない。 */
export function roosterHopLocksFacing(boss: Boss): boolean { return boss.state === 'idle' && roosterHopInAir(boss.roosterHop) }

/** 宙の絵 (跳び越えは頂までが飛び上がり・頂からが宙。前は走り出す絵・下がりは宙の絵)。 */
function airImage(h: RoosterHop, u: number): string {
  if (h.kind === 'over') return u < 0.5 ? FLY_UP : HOVER
  return h.kind === 'fwd' ? LAND_RUN : HOVER
}

/** 絵の側: 待ちの間の跳びの絵。跳んでいない・待ち以外は null。 */
export function roosterHopPoseKey(boss: Boss): string | null {
  const h = boss.roosterHop
  if (boss.state !== 'idle' || !h) return null
  if (h.elapsedMs < h.airMs) return airImage(h, h.elapsedMs / h.airMs)
  return h.elapsedMs < h.airMs + ROOSTER_HOP.landPoseMs ? LAND_RUN : null
}

/** 足の高さ。胴 (首の羽) の山 = risePx·sin(πu) になるよう、絵のずらしも山の形で足す (踏み切り・着地は足が床)。 */
export function roosterHopAltitude(h: RoosterHop): number {
  if (h.elapsedMs >= h.airMs) return 0
  const u = h.elapsedMs / h.airMs
  return Math.max(0, (h.risePx + RUFF_SHIFT[airImage(h, u)]!) * Math.sin(Math.PI * u))
}

/** 横の進み (始めと終わりを少し緩める)。 */
function travelX(h: RoosterHop): number {
  const u = Math.min(1, h.elapsedMs / h.airMs)
  const e = u < 0.5 ? 2 * u * u : 1 - ((-2 * u + 2) ** 2) / 2
  const k = 0.35 * e + 0.65 * u
  return h.fromX + (h.toX - h.fromX) * k
}

/** 待ちを離れたら跳びを片付けて床へ戻す (src/boss.ts の updateBossBase の頭)。
 *  待ちの間は高さを付け直す (頭の settleAltitude が毎 tick 0 にするので、移動の節まで届かない tick でも宙に残す)。 */
export function prepareRoosterHop(boss: Boss): Boss {
  if (!boss.roosterHop) return boss
  if (boss.state === 'idle') return { ...boss, altitudePx: roosterHopAltitude(boss.roosterHop) }
  const { roosterHop: _h, ...rest } = boss
  return { ...rest, altitudePx: 0 }
}

type Bounds = { leftWall: number; rightWall: number }
export type RoosterHopContext = {
  preferredRange: number
  /** 近すぎて下がりたい (立て直し中・好みの間合い − ヒステリシスより近い)。 */
  wantsBack: boolean
  bounds: Bounds
  dtMs: number
}

/** 待ちの1 tick。跳んでいる間と降りた後の待ちの間は攻撃させない (allowAttack=false)。 */
export function updateRoosterHopMove(boss: Boss, player: Player, ctx: RoosterHopContext): { boss: Boss; allowAttack: boolean } {
  const prev = boss.roosterHop
  if (prev && prev.elapsedMs < prev.airMs + prev.restMs) {
    const h = { ...prev, elapsedMs: prev.elapsedMs + ctx.dtMs }
    const x = h.elapsedMs >= h.airMs ? h.toX : travelX(h)
    const busy = h.elapsedMs < h.airMs + h.restMs
    // 降りた tick に向き直る (頭の向き直りは前の tick の宙の状態を見て止まるので、1 コマ遅れて背を向けたまま着かない)。
    const landedNow = prev.elapsedMs < prev.airMs && h.elapsedMs >= h.airMs
    const facing = landedNow ? (player.x < x ? 'left' : 'right') : boss.facing
    return { boss: { ...boss, x, facing, roosterHop: h, altitudePx: roosterHopAltitude(h) }, allowAttack: !busy }
  }
  const dist = Math.abs(player.x - boss.x)
  const toward: 1 | -1 = player.x < boss.x ? -1 : 1
  const n = (prev?.n ?? 0) + 1
  const sinceOver = prev?.sinceOver ?? ROOSTER_HOP.minHopsBetween
  const wantsFwd = dist > ctx.preferredRange
  if (!wantsFwd && !ctx.wantsBack) {
    // 好みの間合いの中: 立ったまま (前の跳びの記録は抽選の種と跳び越えの間隔のために残す)。
    return { boss: prev ? { ...boss, altitudePx: 0 } : boss, allowAttack: true }
  }
  const clampX = (x: number) => Math.max(ctx.bounds.leftWall, Math.min(ctx.bounds.rightWall, x))
  // 跳び越え: 主人公の向こうの好みの間合いへ。壁で降りる所が主人公に近すぎる時は出さない。
  const overX = clampX(player.x + toward * ctx.preferredRange)
  const canOver = sinceOver >= ROOSTER_HOP.minHopsBetween && dist <= ROOSTER_HOP.overMaxDistPx
    && Math.abs(overX - player.x) >= ROOSTER_HOP.overMinLandGapPx
  const roll = hash01(n * 7.31 + boss.attackCount * 3.17)
  let hop: RoosterHop
  if (canOver && roll < ROOSTER_HOP.overChance) {
    hop = { kind: 'over', fromX: boss.x, toX: overX, elapsedMs: 0, airMs: ROOSTER_HOP.overAirMs, restMs: ROOSTER_HOP.overRestMs,
      risePx: ROOSTER_HOP.overRisePx, sinceOver: 0, n }
  } else if (wantsFwd) {
    // 好みの間合いを越えて踏み込まない。
    const step = Math.min(ROOSTER_HOP.fwdPx, dist - ctx.preferredRange + 10)
    hop = { kind: 'fwd', fromX: boss.x, toX: clampX(boss.x + toward * step), elapsedMs: 0, airMs: ROOSTER_HOP.fwdAirMs,
      restMs: ROOSTER_HOP.restMs, risePx: ROOSTER_HOP.fwdRisePx, sinceOver: sinceOver + 1, n }
  } else {
    const step = Math.min(ROOSTER_HOP.backPx, ctx.preferredRange - dist)
    const toX = clampX(boss.x - toward * step)
    // 壁で下がれない: 立ったまま (配布版の滑りも壁で止まる)。
    if (Math.abs(toX - boss.x) < 8) return { boss, allowAttack: true }
    hop = { kind: 'back', fromX: boss.x, toX, elapsedMs: 0, airMs: ROOSTER_HOP.backAirMs,
      restMs: ROOSTER_HOP.restMs, risePx: ROOSTER_HOP.backRisePx, sinceOver: sinceOver + 1, n }
  }
  hop = { ...hop, elapsedMs: ctx.dtMs }
  return { boss: { ...boss, x: travelX(hop), roosterHop: hop, altitudePx: roosterHopAltitude(hop) }, allowAttack: false }
}
